import { useEffect, useState } from "react";
import api, { getToken } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Settings as SettingsIcon, Upload, Loader2, Building2, Percent, ShieldCheck, Globe2, Code, Copy, KeyRound, RotateCcw, FlaskConical, Check, Users, Plus, Trash2, Pencil, Sparkles, Lock } from "lucide-react";

const imgSrc = (url) => (url && url.includes("/api/files/")) ? `${url}${url.includes("?") ? "&" : "?"}auth=${getToken()}` : url;

const FIELDS = [
  { key: "name", label: "Nom de l'atelier", ph: "La Sellerie Tout Simplement" },
  { key: "siret", label: "SIRET", ph: "123 456 789 00012" },
  { key: "vat_number", label: "N° TVA intracom.", ph: "FR12345678900" },
  { key: "address", label: "Adresse", ph: "12 rue des Artisans", full: true },
  { key: "zip", label: "Code postal", ph: "13000" },
  { key: "city", label: "Ville", ph: "Marseille" },
  { key: "phone", label: "Téléphone", ph: "04 91 00 00 00" },
  { key: "email", label: "Email", ph: "contact@atelier.fr" },
  { key: "website", label: "Site web", ph: "https://…", full: true },
];

export default function Settings() {
  const { user, setUser } = useAuth();
  const [company, setCompany] = useState({});
  const [specialtiesText, setSpecialtiesText] = useState("");
  const [minMargin, setMinMargin] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [stsBusy, setStsBusy] = useState(false);

  useEffect(() => {
    api.get("/auth/me").then((r) => {
      const c = r.data.company || { name: r.data.company_name };
      setCompany(c);
      setSpecialtiesText((c.specialties || []).join(", "));
    }).catch(() => {});
    api.get("/settings").then((r) => setMinMargin(r.data.min_margin_pct || 0)).catch(() => {});
  }, []);

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCompany((c) => ({ ...c, logo: data.url }));
      toast.success("Logo téléversé — pensez à enregistrer");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'upload");
    } finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: company.name, logo: company.logo, address: company.address, city: company.city,
        zip: company.zip, phone: company.phone, email: company.email, siret: company.siret,
        vat_number: company.vat_number, website: company.website,
        description_public: company.description_public || "",
        listed: company.listed !== false,
        specialties: specialtiesText.split(",").map((s) => s.trim()).filter(Boolean),
      };
      const { data } = await api.put("/company", payload);
      setCompany(data);
      await api.put("/settings", { min_margin_pct: parseFloat(minMargin) || 0 });
      const me = await api.get("/auth/me");
      setUser(me.data);
      toast.success("Paramètres enregistrés");
    } catch (e) {
      toast.error("Erreur lors de l'enregistrement");
    } finally { setSaving(false); }
  };

  const toggleSts = async () => {
    setStsBusy(true);
    try {
      const approved = company.sts_status === "approved" || company.member_sts;
      const pending = company.sts_status === "pending";
      if (approved || pending) {
        await api.post("/network/leave");
        setCompany((c) => ({ ...c, member_sts: false, sts_status: "none" }));
        toast.success(pending ? "Demande annulée" : "Vous avez quitté le réseau STS");
      } else {
        await api.post("/network/join");
        setCompany((c) => ({ ...c, sts_status: "pending" }));
        toast.success("Demande d'adhésion envoyée — en attente de validation");
      }
    } catch (e) { toast.error("Erreur"); } finally { setStsBusy(false); }
  };

  const embed = `<iframe src="${window.location.origin}/reseau-carte" width="100%" height="520" style="border:0;border-radius:16px" title="Réseau STS"></iframe>`;
  const stsApproved = company.sts_status === "approved" || company.member_sts;
  const stsPending = company.sts_status === "pending";
  const stsLabel = stsApproved ? "Membre" : stsPending ? "En attente" : "Non membre";
  const stsBadgeCls = stsApproved ? "bg-sky-500 text-white" : stsPending ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500";

  return (
    <div className="space-y-6 animate-fade-up max-w-3xl">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-navy flex items-center justify-center"><SettingsIcon className="w-5 h-5 text-sky-400" /></div>
        <div>
          <h1 className="text-3xl font-display font-bold text-navy">Paramètres de l'atelier</h1>
          <p className="text-slate-500">Coordonnées, fiche annuaire, réseau STS et préférences</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="company-profile">
        <h3 className="font-display font-semibold text-navy text-lg inline-flex items-center gap-2"><Building2 className="w-5 h-5 text-sky-500" /> Profil de l'atelier</h3>
        <div className="mt-5 flex items-center gap-4">
          <div className="w-20 h-20 rounded-2xl border border-slate-200 bg-slate-50 overflow-hidden flex items-center justify-center shrink-0">
            {company.logo ? <img src={imgSrc(company.logo)} alt="logo" className="w-full h-full object-contain" data-testid="company-logo-preview" />
              : <Building2 className="w-8 h-8 text-slate-300" />}
          </div>
          <label className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 text-sm font-medium cursor-pointer hover:border-sky-400" data-testid="logo-upload">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Téléverser un logo
            <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files[0])} />
          </label>
        </div>
        <div className="mt-5 grid grid-cols-2 gap-4">
          {FIELDS.map((f) => (
            <div key={f.key} className={f.full ? "col-span-2" : ""}>
              <label className="text-xs font-medium text-slate-500">{f.label}</label>
              <input value={company[f.key] || ""} onChange={(e) => setCompany({ ...company, [f.key]: e.target.value })}
                placeholder={f.ph} data-testid={`company-${f.key}`}
                className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-400" />
            </div>
          ))}
        </div>
      </div>

      {/* Annuaire */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="directory-settings">
        <h3 className="font-display font-semibold text-navy text-lg inline-flex items-center gap-2"><Globe2 className="w-5 h-5 text-sky-500" /> Fiche annuaire public</h3>
        <label className="mt-3 flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={company.listed !== false} onChange={(e) => setCompany({ ...company, listed: e.target.checked })}
            data-testid="listed-toggle" className="accent-sky-400 w-4 h-4" />
          Afficher mon atelier dans l'annuaire public Fabriko
        </label>
        <div className="mt-4">
          <label className="text-xs font-medium text-slate-500">Spécialités (séparées par des virgules)</label>
          <input value={specialtiesText} onChange={(e) => setSpecialtiesText(e.target.value)} data-testid="company-specialties"
            placeholder="Sellerie marine, Bâches sur mesure, Coussins…"
            className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-400" />
        </div>
        <div className="mt-4">
          <label className="text-xs font-medium text-slate-500">Description publique</label>
          <textarea value={company.description_public || ""} onChange={(e) => setCompany({ ...company, description_public: e.target.value })}
            rows={3} data-testid="company-description" placeholder="Présentez votre atelier, votre savoir-faire…"
            className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-400" />
        </div>
        <p className="text-xs text-slate-400 mt-2">La position sur la carte est calculée automatiquement à partir de votre adresse.</p>
      </div>

      {/* Réseau STS */}
      <div className={`rounded-2xl border p-6 ${stsApproved ? "border-sky-300 bg-sky-50" : "border-slate-200 bg-white"}`} data-testid="sts-settings">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display font-semibold text-navy text-lg inline-flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-sky-500" /> Réseau STS — Sellerie Tout Simplement</h3>
            <ul className="mt-3 space-y-1.5 text-sm text-slate-600 list-disc pl-5">
              <li>Réception de missions en sous-traitance du réseau</li>
              <li>Accompagnement par les experts du réseau</li>
              <li>Avantages sur les formations partenaires</li>
              <li>Badge « STS » sur votre fiche annuaire + carte du réseau</li>
            </ul>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-bold shrink-0 ${stsBadgeCls}`} data-testid="sts-status-badge">{stsLabel}</span>
        </div>
        {stsPending && <p className="mt-3 text-sm text-amber-700" data-testid="sts-pending-note">Votre demande d'adhésion est en attente de validation par l'administrateur du réseau.</p>}
        <button onClick={toggleSts} disabled={stsBusy} data-testid="sts-toggle-btn"
          className={`mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold transition-colors disabled:opacity-60 ${stsApproved || stsPending ? "bg-white border border-slate-200 text-slate-600 hover:border-rose-300 hover:text-rose-500" : "bg-navy text-white hover:bg-navy-deep"}`}>
          {stsBusy && <Loader2 className="w-4 h-4 animate-spin" />} {stsApproved ? "Quitter le réseau" : stsPending ? "Annuler la demande" : "Adhérer au réseau STS"}
        </button>

        {stsApproved && (
          <div className="mt-5 pt-5 border-t border-sky-200">
            <div className="text-sm font-semibold text-navy inline-flex items-center gap-2"><Code className="w-4 h-4 text-sky-500" /> Widget carte à intégrer sur le site du réseau</div>
            <div className="mt-2 flex items-start gap-2">
              <textarea readOnly value={embed} data-testid="embed-code" rows={3}
                className="flex-1 px-3 py-2 rounded-xl border border-slate-200 bg-white text-xs font-mono text-slate-600 focus:outline-none" />
              <button onClick={() => { navigator.clipboard?.writeText(embed); toast.success("Code copié"); }} data-testid="copy-embed"
                className="p-2.5 rounded-xl bg-white border border-slate-200 hover:border-sky-300"><Copy className="w-4 h-4 text-sky-600" /></button>
            </div>
            <a href="/reseau-carte" target="_blank" rel="noreferrer" className="text-sm text-sky-600 hover:underline mt-2 inline-block">Prévisualiser la carte →</a>
          </div>
        )}
      </div>

      <SecuritySection />

      <PremiumCard />

      <TeamSection />

      <DemoResetCard />

      <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="margin-settings">
        <h3 className="font-display font-semibold text-navy text-lg inline-flex items-center gap-2"><Percent className="w-5 h-5 text-sky-500" /> Seuil de marge</h3>
        <p className="text-sm text-slate-500 mt-1">Une alerte s'affiche dans les devis dont la marge passe sous ce seuil.</p>
        <div className="mt-3 flex items-center gap-2">
          <input type="number" value={minMargin} onChange={(e) => setMinMargin(e.target.value)} data-testid="min-margin-input"
            className="w-28 px-3 py-2.5 rounded-xl border border-slate-200 font-mono focus:outline-none focus:ring-2 focus:ring-sky-400" />
          <span className="text-slate-500">% minimum</span>
        </div>
      </div>

      <button onClick={save} disabled={saving} data-testid="save-settings"
        className="w-full py-3 rounded-xl bg-navy text-white font-semibold hover:bg-navy-deep transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60">
        {saving && <Loader2 className="w-4 h-4 animate-spin" />} Enregistrer les paramètres
      </button>
    </div>
  );
}

function SecuritySection() {
  const { user, setUser } = useAuth();
  const [step, setStep] = useState("idle");
  const [qr, setQr] = useState(null);
  const [manualKey, setManualKey] = useState("");
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState([]);
  const [busy, setBusy] = useState(false);
  const [pwd, setPwd] = useState("");
  const [disabling, setDisabling] = useState(false);
  const enabled = !!user?.totp_enabled;

  const refresh = async () => { const me = await api.get("/auth/me"); setUser(me.data); };

  const beginSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setQr(data.qr_data_url); setManualKey(data.manual_key); setStep("setup"); setCode("");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/confirm", { code: code.trim() });
      setRecovery(data.recovery_codes || []); setStep("done"); await refresh();
      toast.success("Double authentification activée");
    } catch (e) { toast.error(e.response?.data?.detail || "Code incorrect"); }
    finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { password: pwd });
      setPwd(""); setDisabling(false); setStep("idle"); await refresh();
      toast.success("Double authentification désactivée");
    } catch (e) { toast.error(e.response?.data?.detail || "Mot de passe incorrect"); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="security-settings">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-display font-semibold text-navy text-lg inline-flex items-center gap-2"><KeyRound className="w-5 h-5 text-sky-500" /> Sécurité — Double authentification (2FA)</h3>
          <p className="text-sm text-slate-500 mt-1">Ajoutez un code temporaire (Google Authenticator, Authy…) à votre mot de passe.</p>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-bold shrink-0 ${enabled ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`} data-testid="twofa-status">
          {enabled ? "Activée" : "Désactivée"}
        </span>
      </div>

      {!enabled && step === "idle" && (
        <button onClick={beginSetup} disabled={busy} data-testid="twofa-setup-btn" className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-navy text-white font-semibold hover:bg-navy-deep disabled:opacity-60">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Activer la 2FA
        </button>
      )}

      {!enabled && step === "setup" && (
        <div className="mt-4 grid sm:grid-cols-[auto,1fr] gap-5 items-start">
          <img src={qr} alt="QR code 2FA" className="w-40 h-40 rounded-xl border border-slate-200" data-testid="twofa-qr" />
          <div>
            <p className="text-sm text-slate-600">1. Scannez le QR code dans votre application d'authentification.</p>
            <p className="text-xs text-slate-500 mt-1">Ou saisie manuelle : <code className="font-mono bg-slate-100 px-1.5 py-0.5 rounded">{manualKey}</code></p>
            <label className="block mt-3 text-sm font-medium text-slate-700">2. Entrez le code à 6 chiffres</label>
            <div className="mt-1.5 flex gap-2">
              <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} maxLength={6} data-testid="twofa-code-input"
                placeholder="123456" className="w-40 px-3 py-2.5 rounded-xl border border-slate-200 font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-sky-400" />
              <button onClick={confirm} disabled={busy || code.length !== 6} data-testid="twofa-confirm-btn" className="px-4 py-2.5 rounded-xl bg-navy text-white font-semibold hover:bg-navy-deep disabled:opacity-60 inline-flex items-center gap-2">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} Vérifier
              </button>
            </div>
          </div>
        </div>
      )}

      {step === "done" && recovery.length > 0 && (
        <div className="mt-4 rounded-xl bg-emerald-50 border border-emerald-200 p-4" data-testid="twofa-recovery">
          <div className="flex items-center gap-2 text-emerald-800 font-semibold text-sm"><Check className="w-4 h-4" /> 2FA activée — conservez vos codes de secours</div>
          <p className="text-xs text-emerald-700 mt-1">Chaque code n'est utilisable qu'une fois. Rangez-les en lieu sûr.</p>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
            {recovery.map((c) => <code key={c} className="font-mono text-sm bg-white border border-emerald-200 rounded-lg px-2 py-1.5 text-center text-navy">{c}</code>)}
          </div>
        </div>
      )}

      {enabled && (
        <div className="mt-4">
          {!disabling ? (
            <button onClick={() => setDisabling(true)} data-testid="twofa-disable-btn" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-slate-200 text-rose-600 font-semibold hover:border-rose-300">
              Désactiver la 2FA
            </button>
          ) : (
            <div className="flex flex-wrap items-end gap-2">
              <div>
                <label className="text-xs font-medium text-slate-500">Confirmez votre mot de passe</label>
                <input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} data-testid="twofa-disable-pwd"
                  className="mt-1 w-56 px-3 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-400" />
              </div>
              <button onClick={disable} disabled={busy || !pwd} data-testid="twofa-disable-confirm" className="px-4 py-2.5 rounded-xl bg-rose-500 text-white font-semibold hover:bg-rose-600 disabled:opacity-60">Confirmer</button>
              <button onClick={() => { setDisabling(false); setPwd(""); }} className="px-4 py-2.5 rounded-xl bg-white border border-slate-200 text-slate-600 font-semibold">Annuler</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PremiumCard() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  const toggle = async () => {
    if (!s) return;
    setSaving(true);
    try {
      const next = !s.premium;
      const { data } = await api.put("/settings", {
        min_margin_pct: s.min_margin_pct || 0, monthly_recurring: s.monthly_recurring || 0,
        daily_expense: s.daily_expense || 0, monthly_budget: s.monthly_budget || 0, premium: next,
      });
      setS(data); toast.success(next ? "Licence Premium activée" : "Licence Premium désactivée");
    } catch { toast.error("Erreur"); } finally { setSaving(false); }
  };
  const goPremium = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/premium/checkout", { origin_url: window.location.origin });
      if (data.demo) { toast.success(data.message); const r = await api.get("/settings"); setS(r.data); }
      else if (data.checkout_url) { window.location.href = data.checkout_url; }
    } catch { toast.error("Erreur lors de la création du paiement"); } finally { setSaving(false); }
  };
  const premium = !!s?.premium;
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="premium-settings">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${premium ? "bg-cognac-500" : "bg-slate-100"}`}>
            {premium ? <Sparkles className="w-5 h-5 text-white" /> : <Lock className="w-5 h-5 text-slate-400" />}
          </div>
          <div>
            <h3 className="font-display font-semibold text-navy text-lg">Licence Premium — Sellerie Académie</h3>
            <p className="text-sm text-slate-500 mt-1 max-w-md">Débloque le catalogue de projets types Sellerie Académie et les tutos vidéo / mémos de méthode par étape. La création de vos propres modèles reste gratuite.</p>
            <div className="mt-2 text-xs font-semibold inline-flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-full ${premium ? "bg-emerald-500" : "bg-slate-300"}`} />
              {premium ? <span className="text-emerald-600">Premium actif</span> : <span className="text-slate-400">Premium inactif</span>}
            </div>
          </div>
        </div>
        {premium ? (
          <button onClick={toggle} disabled={saving} data-testid="premium-toggle"
            className="px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-semibold text-sm hover:bg-slate-50 disabled:opacity-60">Désactiver</button>
        ) : (
          <button onClick={goPremium} disabled={saving} data-testid="premium-checkout-btn"
            className="px-4 py-2.5 rounded-xl bg-cognac-500 text-white font-semibold text-sm hover:bg-cognac-600 disabled:opacity-60 inline-flex items-center gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Passer Premium — 29€/mois
          </button>
        )}
      </div>
    </div>
  );
}

function TeamSection() {
  const [cols, setCols] = useState([]);
  const [form, setForm] = useState({ name: "", role: "sellier", capacity: 6, color: "#E8823A" });
  const [editId, setEditId] = useState(null);
  const load = () => api.get("/collaborators").then((r) => setCols(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const ROLES = ["sellier", "tapissier", "garnisseur", "couturier", "coupeur", "apprenti", "autre"];
  const COLORS = ["#E8823A", "#2563EB", "#34A853", "#F5B547", "#8B5CF6", "#EC4899"];
  const reset = () => { setEditId(null); setForm({ name: "", role: "sellier", capacity: 6, color: "#E8823A" }); };
  const save = async () => {
    if (!form.name.trim()) { toast.error("Nom requis"); return; }
    try {
      const payload = { name: form.name, role: form.role, capacity: Number(form.capacity) || 6, color: form.color, active: true };
      if (editId) await api.put(`/collaborators/${editId}`, payload); else await api.post("/collaborators", payload);
      reset(); toast.success("Équipe mise à jour"); load();
    } catch { toast.error("Erreur"); }
  };
  const edit = (c) => { setEditId(c.id); setForm({ name: c.name, role: c.role, capacity: c.capacity, color: c.color }); };
  const remove = async (c) => { if (!window.confirm(`Retirer ${c.name} ?`)) return; await api.delete(`/collaborators/${c.id}`); toast.success("Collaborateur retiré"); load(); };
  const toggleActive = async (c) => { await api.put(`/collaborators/${c.id}`, { name: c.name, role: c.role, capacity: c.capacity, color: c.color, active: c.active === false }); load(); };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="team-settings">
      <h3 className="font-display font-semibold text-navy text-lg inline-flex items-center gap-2"><Users className="w-5 h-5 text-sky-500" /> Équipe & collaborateurs</h3>
      <p className="text-sm text-slate-500 mt-1">Ces collaborateurs alimentent la charge de l'atelier et les responsables d'étapes.</p>
      <div className="mt-4 space-y-2">
        {cols.map((c) => (
          <div key={c.id} className="flex items-center gap-3 rounded-xl border border-slate-100 px-3 py-2.5" data-testid={`collab-row-${c.id}`}>
            <span className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0" style={{ background: c.color }}>{c.name[0]?.toUpperCase()}</span>
            <div className="flex-1 min-w-0"><div className="text-sm font-semibold text-navy truncate">{c.name} {c.active === false && <span className="text-[10px] text-slate-400">(inactif)</span>}</div><div className="text-[11px] text-slate-400 capitalize">{c.role} · capacité {c.capacity}</div></div>
            <button onClick={() => toggleActive(c)} data-testid={`collab-toggle-${c.id}`} className="text-[11px] px-2 py-1 rounded-lg border border-slate-200 text-slate-500 hover:border-sky-300">{c.active === false ? "Activer" : "Désactiver"}</button>
            <button onClick={() => edit(c)} data-testid={`collab-edit-${c.id}`} aria-label="Modifier" className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><Pencil className="w-4 h-4" /></button>
            <button onClick={() => remove(c)} data-testid={`collab-delete-${c.id}`} aria-label="Supprimer" className="p-1.5 rounded-lg hover:bg-rose-50 text-rose-500"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        {cols.length === 0 && <p className="text-sm text-slate-400 py-2">Aucun collaborateur. Ajoutez votre équipe ci-dessous.</p>}
      </div>
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2 items-end">
        <div className="col-span-2 sm:col-span-1"><label className="text-xs text-slate-500">Nom</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="collab-name" className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400" /></div>
        <div><label className="text-xs text-slate-500">Métier</label><select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="collab-role" className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white capitalize">{ROLES.map((r) => <option key={r} value={r}>{r}</option>)}</select></div>
        <div><label className="text-xs text-slate-500">Capacité</label><input type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} data-testid="collab-capacity" className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400" /></div>
        <div><label className="text-xs text-slate-500">Couleur</label><div className="mt-1 flex gap-1">{COLORS.map((col) => <button key={col} onClick={() => setForm({ ...form, color: col })} className={`w-6 h-6 rounded-full ${form.color === col ? "ring-2 ring-offset-1 ring-navy" : ""}`} style={{ background: col }} />)}</div></div>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <button onClick={save} data-testid="collab-save" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-navy text-white font-semibold hover:bg-navy-deep"><Plus className="w-4 h-4" /> {editId ? "Mettre à jour" : "Ajouter le collaborateur"}</button>
        {editId && <button onClick={reset} className="px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-semibold">Annuler</button>}
      </div>
    </div>
  );
}

function DemoResetCard() {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  if (!user?.company?.is_demo) return null;
  const reset = async () => {
    if (!window.confirm("Réinitialiser toutes les données de démonstration ?")) return;
    setBusy(true);
    try { await api.post("/demo/reset"); toast.success("Environnement de démonstration réinitialisé"); setTimeout(() => window.location.reload(), 600); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-amber-300 bg-amber-50 p-6" data-testid="demo-reset-card">
      <h3 className="font-display font-semibold text-navy text-lg inline-flex items-center gap-2"><FlaskConical className="w-5 h-5 text-amber-500" /> Environnement de démonstration</h3>
      <p className="text-sm text-amber-800 mt-1">Ce compte est un bac à sable isolé : données fictives, aucun débit réel, aucun email envoyé. Vous pouvez tout réinitialiser.</p>
      <button onClick={reset} disabled={busy} data-testid="demo-reset-btn" className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-amber-500 text-white font-semibold hover:bg-amber-600 disabled:opacity-60">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />} Réinitialiser la démo
      </button>
    </div>
  );
}
